import { decodeHTMLEntities } from '@skin-seoul/utils';
import { groupBy, mapValues } from 'es-toolkit';
import { ProductStockStatus } from '@skin-seoul/service';
import type { Product } from '@skin-seoul/service';

export interface CategoryOption {
  name: string;
}

/**
 * Processes products data to extract available categories
 * @param products - Array of products to process
 * @param minProductsPerCategory - Minimum number of products required for a category to be included (default: 9)
 * @returns Array of categories that have products in the collection
 */
// Avoid magic numbers by naming the default threshold explicitly
const DEFAULT_MIN_PRODUCTS_PER_CATEGORY = 9 as const;

export const getAvailableCategorizedProducts = (
  products: Product[] = [],
  minProductsPerCategory: number = DEFAULT_MIN_PRODUCTS_PER_CATEGORY,
): CategoryOption[] => {
  const inStock = products.filter((p) => p.status === ProductStockStatus.instock);
  if (inStock.length === 0) {
    return [];
  }

  // Aggregate counts per decoded category name
  const categoryCount = inStock.reduce<Record<string, number>>((acc, product) => {
    if (!Array.isArray(product.category)) {
      return acc;
    }

    product.category.forEach((rawName) => {
      const name = decodeHTMLEntities(String(rawName));
      acc[name] = (acc[name] ?? 0) + 1;
    });

    return acc;
  }, {});

  return transformCategoryCountToOptions(categoryCount, minProductsPerCategory);
};

/**
 * Groups products by category and returns an object where the key is the decoded category name
 * and the value is an array of products belonging to that category. Categories with fewer than
 * `minProductsPerCategory` items are omitted.
 */
export const groupProductsByCategory = (
  products: Product[] = [],
  minProductsPerCategory: number = DEFAULT_MIN_PRODUCTS_PER_CATEGORY,
): Record<string, Product[]> => {
  const inStock = products.filter((p) => p.status === ProductStockStatus.instock);
  if (inStock.length === 0) {
    return {};
  }

  const grouped = groupBy(
    inStock.flatMap((product) =>
      (Array.isArray(product.category) ? product.category : []).map((raw) => ({
        category: decodeHTMLEntities(String(raw)),
        product,
      })),
    ),
    (pair) => pair.category,
  );

  const filtered = Object.fromEntries(
    Object.entries(grouped).filter(([, arr]) => arr.length >= minProductsPerCategory),
  );

  return mapValues(filtered, (arr) => arr.map((pair) => pair.product));
};

/**
 * Internal utility: converts categoryCount map to sorted CategoryOption[]
 */
const transformCategoryCountToOptions = (
  categoryCount: Record<string, number>,
  minProductsPerCategory: number,
): CategoryOption[] => {
  return Object.entries(categoryCount)
    .filter(([, count]) => count >= minProductsPerCategory)
    .sort(([, countA], [, countB]) => countB - countA)
    .map(([name]) => ({ name }));
};
